import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Mic, Menu, X, LayoutDashboard, LogOut, CreditCard } from 'lucide-react';
import LanguageSwitcher from '../common/LanguageSwitcher';
import GlowButton from '../ui/GlowButton';
import './Navbar.css';

export default function Navbar() {
  const { t } = useTranslation();
  const { user, subscription, signOut, hasActivePlan } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/landing');
  };

  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.4.0';

  const scrollToSection = (sectionId) => {
    const isLanding = location.pathname === '/' || location.pathname === '/landing';
    setMobileOpen(false);

    if (isLanding) {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <nav className="landing-navbar">
      <div className="landing-navbar__container">
        {/* Logo con icono y badge de versión */}
        <Link to="/" className="landing-navbar__brand">
          <div className="landing-navbar__icon-wrap">
            <Mic className="w-5 h-5 text-white" />
          </div>
          <span className="landing-navbar__title text-gradient">Garaoke</span>
          <span className="landing-navbar__version">
            v{appVersion}
          </span>
        </Link>

        {/* Links Desktop estilo Fotoevento */}
        <div className="landing-navbar__links">
          <button
            type="button"
            onClick={() => scrollToSection('features')}
            className="landing-navbar__link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('nav.features', 'Características')}
          </button>
          <Link to="/pricing" className="landing-navbar__link">{t('nav.pricing', 'Precios')}</Link>
          <button
            type="button"
            onClick={() => scrollToSection('faq')}
            className="landing-navbar__link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('nav.faq', 'FAQ')}
          </button>
          <Link to="/support" className="landing-navbar__link">{t('nav.support', 'Soporte')}</Link>
        </div>

        {/* Acciones derechas */}
        <div className="landing-navbar__actions">
          <LanguageSwitcher />

          {!user ? (
            <div className="landing-navbar__cta-group">
              <Link to="/login" className="landing-navbar__login-link">
                {t('nav.login', 'Iniciar Sesión')}
              </Link>
              <GlowButton variant="primary" size="sm" onClick={() => navigate('/register')}>
                {t('nav.startFree', 'Comenzar Gratis')}
              </GlowButton>
            </div>
          ) : (
            <div className="landing-navbar__cta-group">
              {hasActivePlan() ? (
                <Link to="/dashboard">
                  <GlowButton variant="primary" size="sm">
                    <LayoutDashboard className="w-4 h-4 mr-1.5 inline" /> {t('nav.dashboard', 'Panel')}
                  </GlowButton>
                </Link>
              ) : (
                <Link to="/pricing">
                  <GlowButton variant="primary" size="sm">
                    <CreditCard className="w-4 h-4 mr-1.5 inline" /> {t('pricing.choosePlan', 'Elegir Plan')}
                  </GlowButton>
                </Link>
              )}
              <button onClick={handleSignOut} className="landing-navbar__signout-btn" title={t('nav.signOut', 'Cerrar Sesión')}>
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Toggle Mobile */}
          <button
            className="landing-navbar__mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Abrir menú"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Menú Mobile */}
      {mobileOpen && (
        <div className="landing-navbar__mobile-menu">
          <button
            type="button"
            onClick={() => scrollToSection('features')}
            className="landing-navbar__mobile-link"
            style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('nav.features', 'Características')}
          </button>
          <Link to="/pricing" onClick={() => setMobileOpen(false)} className="landing-navbar__mobile-link">
            {t('nav.pricing', 'Precios')}
          </Link>
          <button
            type="button"
            onClick={() => scrollToSection('faq')}
            className="landing-navbar__mobile-link"
            style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('nav.faq', 'FAQ')}
          </button>
          <Link to="/support" onClick={() => setMobileOpen(false)} className="landing-navbar__mobile-link">
            {t('nav.support', 'Soporte')}
          </Link>
          <div className="landing-navbar__mobile-actions">
            {!user ? (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="landing-navbar__mobile-link">
                  {t('nav.login', 'Iniciar Sesión')}
                </Link>
                <GlowButton variant="primary" size="md" onClick={() => { setMobileOpen(false); navigate('/register'); }}>
                  {t('nav.startFree', 'Comenzar Gratis')}
                </GlowButton>
              </>
            ) : (
              <>
                {hasActivePlan() ? (
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                    <GlowButton variant="primary" size="md">
                      {t('nav.dashboard', 'Ir al Karaoke')}
                    </GlowButton>
                  </Link>
                ) : (
                  <Link to="/pricing" onClick={() => setMobileOpen(false)}>
                    <GlowButton variant="primary" size="md">
                      {t('pricing.choosePlan', 'Elegir Plan')}
                    </GlowButton>
                  </Link>
                )}
                <button onClick={handleSignOut} className="landing-navbar__mobile-link" style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {t('nav.signOut', 'Cerrar Sesión')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
